import React, { useEffect, useState } from 'react';
import { useAuth } from './hooks/useAuth';
import App from './App';
import Paywall from './components/Paywall';
import PrivacyConsent from './components/PrivacyConsent';
import { getApiUrl, fetchWithRetry } from './services/api';

const AppGate: React.FC = () => {
  const { subscriptionStatus, login, selectFreeTier, isLoading: isAuthLoading, startTrial } = useAuth();
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  
  // Initialize state directly from localStorage to see if consent has been given.
  const [hasAcceptedPrivacy, setHasAcceptedPrivacy] = useState(
    () => localStorage.getItem('privacyConsentAccepted') === 'true'
  );

  useEffect(() => {
    const verifyServerSidePayment = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const stripeSessionId = urlParams.get('session_id');
      const paypalSubscriptionId = urlParams.get('subscription_id');

      let provider: 'stripe' | 'paypal' | null = null;
      let sessionId: string | null = null;

      if (stripeSessionId) {
          provider = 'stripe';
          sessionId = stripeSessionId;
      } else if (paypalSubscriptionId) {
          provider = 'paypal';
          sessionId = paypalSubscriptionId;
      }

      if (provider && sessionId) {
        setIsVerifying(true);
        setVerificationError(null);
        try {
          const response = await fetchWithRetry(await getApiUrl('/verify-payment'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ provider, sessionId }),
          });
          
          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || 'Failed to verify payment session.');
          }

          if (data.status === 'success') {
            await login({
              provider: data.provider,
              duration: data.duration,
              customerId: data.customerId,
              subscriptionId: data.subscriptionId,
            });
            // Navigate to the clean app URL to clear query params and reload the app state.
            // This prevents a potential infinite reload loop.
            window.location.href = '/app.html';
            return; // Stop execution as the page will navigate.
          } else {
             throw new Error('Payment verification was not successful.');
          }

        } catch (error: any) {
          console.error("Payment verification failed:", error);
          setVerificationError(error.message);
          // If verification fails, clean up the URL and stop the loading spinner.
          window.history.replaceState({}, document.title, window.location.pathname);
          setIsVerifying(false);
        }
      }
    };

    verifyServerSidePayment();
  }, [login]);

  const handleAcceptPrivacy = () => {
    localStorage.setItem('privacyConsentAccepted', 'true');
    setHasAcceptedPrivacy(true);
    startTrial();
  };
  
  const LoadingSpinner = () => (
     <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col items-center justify-center text-center p-4">
        <svg className="animate-spin h-10 w-10 text-teal-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <h1 className="text-2xl font-semibold text-slate-700 dark:text-slate-200">
          {isVerifying ? 'Verifying your payment...' : 'Loading...'}
        </h1>
      </div>
  );

  // The very first screen the user sees, if they haven't consented yet.
  if (!hasAcceptedPrivacy) {
    return <PrivacyConsent onAccept={handleAcceptPrivacy} />;
  }

  // While checking auth status or verifying payment, show a loading state.
  if (isAuthLoading || isVerifying) {
    return <LoadingSpinner />;
  }

  // If status is null, user hasn't made a choice or subscription expired. Show paywall.
  if (!subscriptionStatus) {
    return <Paywall onSelectFreeTier={selectFreeTier} initialError={verificationError ?? undefined} />;
  }

  // If status is 'subscribed' or 'free', show the app.
  return <App />;
};

export default AppGate;
